import React, { useEffect, useRef } from "react";

import { isMobile } from "react-device-detect";
import {
  BlankButton,
  ModalStepProgressBar,
  PrimaryButton,
  SecondaryButton,
  SpinnerButton,
} from "..";
import { useModalContext } from "../../contexts/ModalContext";
import { useUploadContext } from "../../contexts/UploadContext";
import { submitDataUpload } from "../../features/UploadData/services/ApiUploadFunctions";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { uploadSessionFormSlice } from "../../redux/slices/uploadSessionFormSlice";
import {
  usePatchSessionUploadMutation,
  usePostSessionUploadMutation,
} from "../../services";
import { emptyModalState } from "../../shared/Constants";
import { joinClassNames } from "../../shared/Functions";
import { FormTypeEnum } from "../../types";

export type StepType = {
  title: string;
  display: JSX.Element;
  disableNext: boolean;
  formType: FormTypeEnum;
};

type ModalStepProps = {
  steps: StepType[];
};

export const ModalStep: React.FC<ModalStepProps> = ({ steps }) => {
  // State and AppContext
  const {
    setUploadProgressState,
    uploadFiles,
    setAbortControllers,
    setUploadInProgress,
  } = useUploadContext();
  const { modalState, setModalState } = useModalContext();
  const { currentStep, title: modalTitle } = modalState;
  const [display, setDisplay] = React.useState<JSX.Element>(<></>);
  const [disableNext, setDisableNext] = React.useState<boolean>(true);
  const [sessionCreated, setSessionCreated] = React.useState(false);
  const lastStep = steps.length;
  const secondToLastStep = steps.length - 1;

  const stepSelector = useRef<HTMLDivElement>(null);

  // Redux calls
  const uploadSessionFormValues = useAppSelector(
    (state) => state.uploadSessionForm,
  );

  // RTK slice actions
  const dispatch = useAppDispatch();
  const { clearUploadSessionFormState } = uploadSessionFormSlice.actions;

  // RTK Queries and Mutations
  const [postSessionUpload, { isLoading }] = usePostSessionUploadMutation();
  const [patchSessionUpload] = usePatchSessionUploadMutation();

  const goNextStep = () => {
    const nextStep = currentStep + 1;
    if (steps && nextStep <= steps.length) {
      setModalState((prev) => ({
        ...prev,
        currentStep: nextStep,
      }));
    }
  };

  const goPreviousStep = () => {
    const previousStep = currentStep - 1;
    if (previousStep >= 1) {
      setModalState((prev) => ({
        ...prev,
        currentStep: previousStep,
      }));
    }
  };

  const dataUploadProps = {
    dispatch,
    uploadSessionFormValues,
    postSessionUpload,
    patchSessionUpload,
    setUploadProgressState,
    setModalState,
    uploadFiles,
    setAbortControllers,
    setUploadInProgress,
    goPreviousStep,
  };

  const handleUploadAndContinue = () => {
    submitDataUpload({ ...dataUploadProps, uploadMoreMovements: true });
    setSessionCreated(true);
  };

  const handleUploadAndFinish = () => {
    if (currentStep === lastStep && modalTitle.includes("Upload Data")) {
      submitDataUpload({ ...dataUploadProps, uploadMoreMovements: false });
    } else {
      goNextStep();
    }
  };

  const closeModal = () => {
    dispatch(clearUploadSessionFormState());
    setModalState(emptyModalState);
  };

  useEffect(() => {
    if (steps && steps.length > 0) {
      setDisplay(steps[currentStep - 1].display);
      setDisableNext(steps[currentStep - 1].disableNext);
    }
  }, [currentStep, steps]);

  return (
    <div
      data-testid="multi-step-modal-contents"
      className="w-full relative flex flex-col justify-between h-full"
    >
      <ModalStepProgressBar currentStep={currentStep} steps={steps} />
      <div
        className={joinClassNames(
          isMobile ? "h-full" : "h-[471px]",
          "w-full flex justify-center overflow-scroll",
        )}
        ref={stepSelector}
        data-testid="multi-step-modal-body"
      >
        {display}
      </div>
      <footer
        id="footer-container"
        className={joinClassNames(
          currentStep === 1 ? "justify-end" : "justify-between",
          "flex pt-5 border-t border-gray-200 h-20 py-4 px-6",
        )}
        data-testid="multi-step-modal-footer"
      >
        <BlankButton hidden={currentStep === 1} onClick={goPreviousStep}>
          Back
        </BlankButton>
        <div className="flex items-center">
          {currentStep === lastStep && !isLoading ? (
            <SecondaryButton
              onClick={handleUploadAndContinue}
              disabled={disableNext}
              className={isMobile ? "ml-3" : ""}
            >
              {isMobile ? "Upload & Add" : "Upload & Add Another Movement Type"}
            </SecondaryButton>
          ) : null}
          {sessionCreated && currentStep === secondToLastStep && (
            <SecondaryButton onClick={closeModal} className="ml-3">
              Finish
            </SecondaryButton>
          )}
          {isLoading ? (
            <div className="ml-3">
              <SpinnerButton btnText={"Uploading"} />
            </div>
          ) : (
            <PrimaryButton
              onClick={handleUploadAndFinish}
              className="ml-3"
              disabled={disableNext}
            >
              {currentStep === lastStep ? "Upload & Finish" : "Next"}
            </PrimaryButton>
          )}
        </div>
      </footer>
    </div>
  );
};
